import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import DepartmentForm from "@/modules/departments/components/DepartmentForm";
import { useDepartmentsDictionary } from "@/modules/departments/hooks/useDepartmentsDictionary";
import { ApiError } from "@/services/api";
import { ReadDepartment, WriteDepartment } from "@/types/department";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useEffect } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function DepartmentEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = useDepartmentsDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.UpdatesDepartments,
  );
  const { can_update } = useModulePermissions();
  const query = useQuery<ReadDepartment>({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => {
      const result = await config.service.get(id);
      return result as ReadDepartment;
    },
  });

  const mutationFn: MutationFunction<ReadDepartment, WriteDepartment> = async (
    values,
  ) => {
    const response = (await config.service.patch(id, values)) as ReadDepartment;

    return {
      ...values,
      id: response?.id,
    } as unknown as ReadDepartment;
  };

  const mutation = useMutation<ReadDepartment, ApiError, WriteDepartment>({
    mutationFn,
    onSuccess: async (response) => {
      const id = response.id;

      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      updateQueryDataForExistingItem(response);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WriteDepartment>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<WriteDepartment>,
    disabled: mutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);

  useEffect(() => {
    if (query.data) {
      const formValues = Object.fromEntries(
        Object.entries(query.data).filter(([, value]) => value != null),
      ) as DefaultValues<WriteDepartment>;
      form.reset(formValues);
    }
  }, [form, query.data]);

  return (
    <DictionaryRecordContextProvider
      value={{
        query,
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && <DepartmentForm />}
    </DictionaryRecordContextProvider>
  );
}
